import {
  Activity,
  Boxes,
  Briefcase,
  Check,
  Eye,
  Layers,
  Lock,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  useAccess,
  useCreateRole,
  useDeleteRole,
  useMemberDirectory,
  useResetRoleDefaults,
  useUpdateRole,
  type RoleOut,
} from "@/api/queries";
import { useCan, useMember } from "@/auth/member";
import { CreateRoleModal } from "@/components/access/CreateRoleModal";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Menu } from "@/components/ui/menu";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { Switch } from "@/components/ui/switch";
import { managerLabel, memberLabel } from "@/lib/member-label";
import { cn } from "@/lib/utils";

const MEMBER_NUMBER = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;
const fold = (m: string) => m.toLowerCase();

function sameArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a.map(fold));
  return b.every((x) => s.has(fold(x)));
}

function sameSet(a: string[], b: string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const x of sa) {
    if (!sb.has(x)) return false;
  }
  return true;
}

function rolesByMember(roles: RoleOut[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const r of roles) {
    for (const m of r.members) {
      out.set(fold(m), [...(out.get(fold(m)) ?? []), r.title || r.role]);
    }
  }
  return out;
}

const ROLE_ICONS: Record<string, typeof ShieldCheck> = {
  admin: ShieldCheck,
  maintainer: Wrench,
  resource_manager: Briefcase,
  viewer: Eye,
};

const KNOWN_MODULES: Record<string, { label: string; icon: typeof Layers; description: string }> = {
  daily: {
    label: "Daily Operations (Questions & Review)",
    icon: Layers,
    description: "Review workflow, question management, deduplication clusters, and document ingest.",
  },
  jds: {
    label: "Job Descriptions (JDs & Gaps)",
    icon: Briefcase,
    description: "Client job descriptions, automated skill extraction, gap reports, and aliases.",
  },
  taxonomy: {
    label: "Taxonomy & Vocabularies",
    icon: Boxes,
    description: "Stacks, concepts, and evaluation buckets.",
  },
  tools: {
    label: "Assessment Instruments & Tools",
    icon: Activity,
    description: "Question Picker simulation, Similarity Probe, and metrics diagnostics.",
  },
  admin: {
    label: "Administration & Security",
    icon: ShieldCheck,
    description: "Role definitions, permissions studio, system configuration, and maintenance.",
  },
};

function getModuleMeta(modKey: string) {
  if (KNOWN_MODULES[modKey]) return KNOWN_MODULES[modKey];
  // Auto-format unknown / future modules dynamically:
  const formatted = modKey
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    label: formatted,
    icon: Sparkles,
    description: `Capabilities and page access controls for ${formatted}.`,
  };
}

const KIND_LABELS: Record<string, string> = {
  read: "Read / View Access",
  create: "Create & Add Access",
  edit: "Update & Edit Access",
  delete: "Delete Access",
  export: "Export & Reports",
  execute: "Operations & Execution",
  admin: "Administrative Controls",
};

interface RoleDraft {
  members: string[];
  permissions: string[];
  title: string;
  purpose: string;
  note: string;
}

export default function Access() {
  const canManage = useCan("manage_access") || useCan("access:roles_edit");
  const me = useMember();
  const access = useAccess(canManage);
  const directory = useMemberDirectory(canManage);

  const [selectedRole, setSelectedRole] = useState<string>("admin");
  const [activeTab, setActiveTab] = useState<"permissions" | "members" | "matrix">("permissions");
  const [roleDrafts, setRoleDrafts] = useState<Record<string, RoleDraft>>({});

  // Dialogs
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteTargetRole, setDeleteTargetRole] = useState<RoleOut | null>(null);
  const [resetDefaultsRole, setResetDefaultsRole] = useState<RoleOut | null>(null);

  // Member search & add entry
  const [memberSearch, setMemberSearch] = useState("");
  const [entry, setEntry] = useState("");
  const [entryError, setEntryError] = useState<string | null>(null);

  // Matrix filter
  const [matrixModuleFilter, setMatrixModuleFilter] = useState<string>("all");

  const updateRole = useUpdateRole();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();
  const resetDefaults = useResetRoleDefaults();

  const roles = useMemo(() => access.data?.roles ?? [], [access.data]);
  const catalog = useMemo(() => access.data?.catalog ?? [], [access.data]);
  const dynamicModules = useMemo(() => {
    const list: string[] = [];
    for (const item of catalog) {
      if (!list.includes(item.module)) {
        list.push(item.module);
      }
    }
    return list;
  }, [catalog]);

  const moduleFilterOptions = useMemo(
    () => [
      { value: "all", label: `All Modules (${catalog.length})` },
      ...dynamicModules.map((m) => ({
        value: m,
        label: getModuleMeta(m).label,
      })),
    ],
    [dynamicModules, catalog.length],
  );

  const memberRolesMap = useMemo(() => rolesByMember(roles), [roles]);

  // Names are looked up from Workspace, never stored: only the member number is saved.
  const directoryByNumber = useMemo(
    () => new Map((directory.data?.members ?? []).map((m) => [fold(m.member_no), m])),
    [directory.data],
  );
  const directoryReady = directory.data?.available === true;

  const activeRoleData = useMemo(() => {
    return roles.find((r) => r.role === selectedRole) ?? roles[0];
  }, [roles, selectedRole]);

  // Keep draft state in sync or initialize
  const activeDraft: RoleDraft = useMemo(() => {
    if (!activeRoleData) {
      return { members: [], permissions: [], title: "", purpose: "", note: "" };
    }
    const current = roleDrafts[activeRoleData.role];
    if (current) return current;

    // Sanitize initial permissions: ensure child permissions only appear if their parent_page is active,
    // and ensure active pages have their baseline child read permission auto-included.
    const rawPerms = activeRoleData.permissions;
    const activePages = new Set(
      catalog.filter((p) => p.kind === "page" && rawPerms.includes(p.key)).map((p) => p.key),
    );
    const sanitizedPerms = rawPerms.filter((k) => {
      const pDef = catalog.find((p) => p.key === k);
      if (pDef?.parent_page) {
        return activePages.has(pDef.parent_page);
      }
      return true;
    });

    for (const pageKey of activePages) {
      const childRead = catalog.find((p) => p.parent_page === pageKey && p.kind === "read");
      if (childRead && !sanitizedPerms.includes(childRead.key)) {
        sanitizedPerms.push(childRead.key);
      }
    }

    return {
      members: activeRoleData.members,
      permissions: sanitizedPerms,
      title: activeRoleData.title || activeRoleData.role,
      purpose: activeRoleData.purpose || "",
      note: activeRoleData.note || "",
    };
  }, [activeRoleData, roleDrafts, catalog]);

  // Members already on this role are left out, so picking one is always an addition.
  const pickerOptions = useMemo(() => {
    const held = new Set(activeDraft.members.map(fold));
    return (directory.data?.members ?? [])
      .filter((m) => !held.has(fold(m.member_no)))
      .map((m) => ({
        value: m.member_no,
        // The number trails the label so the picker can still be searched by it.
        label: `${memberLabel(m)} · #${m.member_no}`,
      }));
  }, [directory.data, activeDraft.members]);

  if (!canManage) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <h1 className="text-base font-semibold tracking-tight">Access</h1>
        <Callout>Only administrators with access management rights can view and configure roles.</Callout>
      </div>
    );
  }

  const isMe = (m: string) => fold(m) === fold(me.member_no);

  function setDraft(updater: (prev: RoleDraft) => RoleDraft) {
    if (!activeRoleData) return;
    setRoleDrafts((prev) => ({
      ...prev,
      [activeRoleData.role]: updater(activeDraft),
    }));
  }

  const isDirty = useMemo(() => {
    if (!activeRoleData) return false;
    const membersChanged = !sameArray(activeDraft.members, activeRoleData.members);
    const permsChanged = !sameSet(activeDraft.permissions, activeRoleData.permissions);
    const titleChanged = activeDraft.title !== (activeRoleData.title || activeRoleData.role);
    const purposeChanged = activeDraft.purpose !== (activeRoleData.purpose || "");
    const noteChanged = activeDraft.note !== (activeRoleData.note || "");
    return membersChanged || permsChanged || titleChanged || purposeChanged || noteChanged;
  }, [activeRoleData, activeDraft]);

  function handleSave() {
    if (!activeRoleData) return;
    // Ensure active pages always persist with their baseline read permission
    const activePages = new Set(
      catalog.filter((p) => p.kind === "page" && activeDraft.permissions.includes(p.key)).map((p) => p.key),
    );
    const finalPerms = new Set(activeDraft.permissions);
    for (const pageKey of activePages) {
      const childRead = catalog.find((p) => p.parent_page === pageKey && p.kind === "read");
      if (childRead) {
        finalPerms.add(childRead.key);
      }
    }

    updateRole.mutate(
      {
        role: activeRoleData.role,
        title: activeDraft.title,
        purpose: activeDraft.purpose,
        members: activeDraft.members,
        expectedMembers: activeRoleData.members,
        permissions: Array.from(finalPerms),
        note: activeDraft.note,
      },
      {
        onSuccess: () => {
          setRoleDrafts((prev) => {
            const next = { ...prev };
            delete next[activeRoleData.role];
            return next;
          });
        },
      },
    );
  }

  function handleDiscard() {
    if (!activeRoleData) return;
    setRoleDrafts((prev) => {
      const next = { ...prev };
      delete next[activeRoleData.role];
      return next;
    });
    setEntry("");
    setEntryError(null);
  }

  function handleAddMembers() {
    const tokens = entry
      .split(/[,;\s]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const bad = tokens.filter((t) => !MEMBER_NUMBER.test(t));
    if (bad.length > 0) {
      setEntryError(`Not member numbers: ${bad.join(", ")}. Use letters, digits, ".", "_" or "-" only.`);
      return;
    }
    const known = new Set(activeDraft.members.map(fold));
    const toAdd: string[] = [];
    for (const t of tokens) {
      if (!known.has(fold(t))) {
        known.add(fold(t));
        toAdd.push(t);
      }
    }
    if (toAdd.length > 0) {
      setDraft((d) => ({ ...d, members: [...d.members, ...toAdd] }));
    }
    setEntry("");
    setEntryError(null);
  }

  // The picker commits a Workspace member number, or whatever was typed that matched nobody. A
  // typed value is only kept when it looks like a number: the picker commits on blur, and half a
  // name ("Dip") would otherwise be saved as a member.
  function handlePickMember(value: string) {
    const picked = value.trim();
    if (!picked) return;
    if (!directoryByNumber.has(fold(picked)) && !(MEMBER_NUMBER.test(picked) && /\d/.test(picked))) {
      setEntryError(`No Workspace member matches "${picked}". Pick from the list, or type a member number.`);
      return;
    }
    if (!activeDraft.members.some((m) => fold(m) === fold(picked))) {
      setDraft((d) => ({ ...d, members: [...d.members, picked] }));
    }
    setEntryError(null);
  }

  function handleTogglePermission(permKey: string) {
    if (!activeRoleData) return;
    // Invariant: admin role cannot revoke access management
    if (activeRoleData.role === "admin" && (permKey === "access:roles_edit" || permKey === "page:access")) {
      return;
    }
    const permDef = catalog.find((p) => p.key === permKey);
    const has = activeDraft.permissions.includes(permKey);

    if (permDef?.kind === "page") {
      if (has) {
        // Turning page OFF: cascade remove page AND all child permissions belonging to this page
        setDraft((d) => ({
          ...d,
          permissions: d.permissions.filter((k) => {
            if (k === permKey) return false;
            const target = catalog.find((p) => p.key === k);
            return target?.parent_page !== permKey;
          }),
        }));
      } else {
        // Turning page ON: auto-include baseline view / read permission
        const childRead = catalog.find((p) => p.parent_page === permKey && p.kind === "read");
        setDraft((d) => {
          const next = new Set(d.permissions);
          next.add(permKey);
          if (childRead) {
            next.add(childRead.key);
          }
          return {
            ...d,
            permissions: Array.from(next),
          };
        });
      }
    } else {
      // Child / action permission:
      if (permDef?.parent_page && !activeDraft.permissions.includes(permDef.parent_page)) {
        // Cannot enable child without parent page enabled
        return;
      }
      // Baseline read / view permissions are locked with the screen and cannot be untoggled individually
      if (permDef?.kind === "read") {
        return;
      }
      setDraft((d) => ({
        ...d,
        permissions: has ? d.permissions.filter((p) => p !== permKey) : [...d.permissions, permKey],
      }));
    }
  }

  function handleTogglePageChildAll(pageKey: string, enable: boolean) {
    if (!activeRoleData) return;
    const childPerms = catalog.filter((p) => p.parent_page === pageKey);
    const childPermKeys = childPerms.map((p) => p.key);
    setDraft((d) => {
      let next = new Set(d.permissions);
      if (enable) {
        next.add(pageKey);
        childPermKeys.forEach((k) => next.add(k));
      } else {
        // Deselect optional mutation actions while keeping the baseline view permission intact
        childPerms.forEach((p) => {
          if (p.kind === "read") {
            next.add(p.key);
            return;
          }
          if (activeRoleData.role === "admin" && (p.key === "access:roles_edit" || p.key === "page:access")) {
            return;
          }
          next.delete(p.key);
        });
      }
      return { ...d, permissions: Array.from(next) };
    });
  }

  function handleToggleModuleAll(moduleKey: string, enable: boolean) {
    if (!activeRoleData) return;
    const modulePerms = catalog.filter((p) => p.module === moduleKey).map((p) => p.key);
    setDraft((d) => {
      let next = new Set(d.permissions);
      if (enable) {
        modulePerms.forEach((k) => next.add(k));
      } else {
        modulePerms.forEach((k) => {
          if (activeRoleData.role === "admin" && (k === "access:roles_edit" || k === "page:access")) {
            return;
          }
          next.delete(k);
        });
      }
      return { ...d, permissions: Array.from(next) };
    });
  }

  function handleToggleModulePages(moduleKey: string, enable: boolean) {
    if (!activeRoleData) return;
    const pagePermKeys = catalog.filter((p) => p.module === moduleKey && p.kind === "page").map((p) => p.key);
    const childPerms = catalog.filter((p) => p.module === moduleKey && p.parent_page && pagePermKeys.includes(p.parent_page));
    const childPermKeys = childPerms.map((p) => p.key);
    const childReadPermKeys = childPerms.filter((p) => p.kind === "read").map((p) => p.key);

    setDraft((d) => {
      let next = new Set(d.permissions);
      if (enable) {
        pagePermKeys.forEach((k) => next.add(k));
        childReadPermKeys.forEach((k) => next.add(k));
      } else {
        pagePermKeys.forEach((k) => {
          if (activeRoleData.role === "admin" && (k === "access:roles_edit" || k === "page:access")) {
            return;
          }
          next.delete(k);
        });
        childPermKeys.forEach((k) => {
          if (activeRoleData.role === "admin" && (k === "access:roles_edit" || k === "page:access")) {
            return;
          }
          next.delete(k);
        });
      }
      return { ...d, permissions: Array.from(next) };
    });
  }

  // Filtered members for member tab
  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return activeDraft.members;
    const q = memberSearch.toLowerCase();
    return activeDraft.members.filter(
      (m) =>
        m.toLowerCase().includes(q) || (directoryByNumber.get(fold(m))?.full_name ?? "").toLowerCase().includes(q),
    );
  }, [activeDraft.members, memberSearch, directoryByNumber]);
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Studio Top Banner */}
      <div className="border-bank-border bg-bank-surface flex shrink-0 items-center justify-between border-b px-5 py-3.5">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold tracking-tight">Access & Roles Studio</h1>
            <span className="bg-bank-accent-wash text-bank-accent-ink rounded px-2 py-0.5 font-mono text-[11px] font-medium">
              Dynamic RBAC
            </span>
          </div>
          <p className="text-bank-muted text-xs">
            Configure dynamic role definitions, granular CRUD and page access permissions, and member assignments.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="primary" onClick={() => setCreateModalOpen(true)}>
            <Plus className="mr-1.5 size-3.5" aria-hidden="true" />
            New Role
          </Button>
        </div>
      </div>

      <ErrorNote error={access.error} />
      {access.isLoading && <Empty>Loading Access & Permissions Studio…</Empty>}

      {access.data && activeRoleData && (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left Rail: Roles List */}
          <aside className="border-bank-border bg-bank-surface-2 flex w-64 shrink-0 flex-col border-r">
            <div className="border-bank-border flex items-center justify-between border-b p-3">
              <span className="text-bank-muted font-mono text-[11px] font-medium tracking-[0.08em] uppercase">
                Roles ({roles.length})
              </span>
            </div>

            <div className="flex-1 space-y-1 overflow-y-auto p-2">
              {roles.map((r) => {
                const isSelected = r.role === activeRoleData.role;
                const Icon = ROLE_ICONS[r.role] || Sparkles;
                const draft = roleDrafts[r.role];
                const memberCount = draft?.members ? draft.members.length : r.members.length;
                const hasDirty = draft !== undefined;

                return (
                  <div
                    key={r.role}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedRole(r.role)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setSelectedRole(r.role);
                    }}
                    className={cn(
                      "group relative flex cursor-pointer items-start justify-between rounded-lg p-2.5 transition-all text-left",
                      isSelected
                        ? "bg-bank-surface border-bank-border-strong border shadow-xs"
                        : "hover:bg-bank-surface/60 border border-transparent",
                    )}
                  >
                    {isSelected && (
                      <span className="bg-bank-accent absolute top-2 bottom-2 left-0 w-1 rounded-r" aria-hidden="true" />
                    )}

                    <div className="flex min-w-0 items-start gap-2.5">
                      <div
                        className={cn(
                          "grid size-8 shrink-0 place-items-center self-center rounded-md border",
                          isSelected
                            ? "bg-bank-accent-wash text-bank-accent-ink border-bank-accent/30"
                            : "bg-bank-surface text-bank-muted border-bank-border",
                        )}
                      >
                        <Icon className="size-4" aria-hidden="true" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-xs font-semibold leading-tight">{r.title || r.role}</p>
                          {hasDirty && (
                            <span className="size-1.5 shrink-0 rounded-full bg-bank-attention" title="Unsaved changes" />
                          )}
                        </div>
                        <p className="text-bank-muted truncate font-mono text-[10px]">{r.role}</p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-bank-muted font-mono text-[10px] tabular-nums">
                            {memberCount} {memberCount === 1 ? "member" : "members"}
                          </span>
                          <span
                            className={cn(
                              "rounded px-1 py-0.2 font-mono text-[9px] uppercase",
                              r.is_builtin
                                ? "bg-bank-surface-3 text-bank-muted"
                                : "bg-bank-accent-wash text-bank-accent-ink",
                            )}
                          >
                            {r.is_builtin ? "system" : "custom"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {!r.is_builtin && (
                      <div className="opacity-0 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                        <Menu
                          label={`Actions for ${r.title}`}
                          variant="ghost"
                          size="sm"
                          iconOnly
                          trigger={<Trash2 className="size-3.5 text-bank-bad-ink" aria-hidden="true" />}
                          items={[
                            {
                              label: "Delete Role",
                              hint: "Delete this custom role permanently",
                              danger: true,
                              onSelect: () => setDeleteTargetRole(r),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Right Main Area: Workspace Studio */}
          <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
            {/* Role Header Banner */}
            <div className="border-bank-border bg-bank-surface space-y-3 border-b p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold tracking-tight">
                      {activeDraft.title || activeRoleData.title || activeRoleData.role}
                    </h2>
                    <span className="text-bank-muted font-mono text-xs">({activeRoleData.role})</span>
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase",
                        activeRoleData.is_builtin
                          ? "bg-bank-surface-3 text-bank-muted"
                          : "bg-bank-accent-wash text-bank-accent-ink",
                      )}
                    >
                      {activeRoleData.is_builtin ? "System Built-in" : "Custom Role"}
                    </span>
                  </div>
                  <p className="text-bank-muted text-xs">
                    {activeDraft.purpose || activeRoleData.purpose || "No description provided."}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {activeRoleData.is_builtin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={resetDefaults.isPending}
                      onClick={() => setResetDefaultsRole(activeRoleData)}
                    >
                      <RotateCcw className="mr-1 size-3.5" aria-hidden="true" />
                      Reset to Defaults
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={!isDirty || updateRole.isPending} onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button size="sm" variant="primary" disabled={!isDirty || updateRole.isPending} onClick={handleSave}>
                    {updateRole.isPending ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>

              {/* Tabs Bar */}
              <div className="border-bank-border flex items-center gap-6 border-t pt-3">
                <button
                  type="button"
                  onClick={() => setActiveTab("permissions")}
                  className={cn(
                    "relative pb-1.5 text-xs font-medium transition-colors",
                    activeTab === "permissions"
                      ? "text-bank-accent-ink font-semibold"
                      : "text-bank-muted hover:text-bank-ink",
                  )}
                >
                  Permissions & Page Access ({activeDraft.permissions.length})
                  {activeTab === "permissions" && (
                    <span className="bg-bank-accent absolute right-0 bottom-0 left-0 h-0.5 rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("members")}
                  className={cn(
                    "relative pb-1.5 text-xs font-medium transition-colors",
                    activeTab === "members"
                      ? "text-bank-accent-ink font-semibold"
                      : "text-bank-muted hover:text-bank-ink",
                  )}
                >
                  Assigned Members ({activeDraft.members.length})
                  {activeTab === "members" && (
                    <span className="bg-bank-accent absolute right-0 bottom-0 left-0 h-0.5 rounded-full" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("matrix")}
                  className={cn(
                    "relative pb-1.5 text-xs font-medium transition-colors",
                    activeTab === "matrix"
                      ? "text-bank-accent-ink font-semibold"
                      : "text-bank-muted hover:text-bank-ink",
                  )}
                >
                  Role Comparison Matrix
                  {activeTab === "matrix" && (
                    <span className="bg-bank-accent absolute right-0 bottom-0 left-0 h-0.5 rounded-full" />
                  )}
                </button>
              </div>
            </div>

            {/* Tab 1: Permissions & Page Access */}
            {activeTab === "permissions" && (
              <div className="space-y-4 p-5">
                {activeRoleData.role === "admin" && (
                  <Callout tone="info">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Lock className="size-3.5" aria-hidden="true" />
                      Core Invariant Safeguard
                    </span>
                    The Admin role always retains access management and security permissions to prevent system lockouts.
                  </Callout>
                )}

                {dynamicModules.map((modKey) => {
                  const modMeta = getModuleMeta(modKey);
                  const modPerms = catalog.filter((p) => p.module === modKey);
                  const pagePerms = modPerms.filter((p) => p.kind === "page");
                  const activePageCount = pagePerms.filter((p) => activeDraft.permissions.includes(p.key)).length;
                  const allPagesActive = pagePerms.length > 0 && activePageCount === pagePerms.length;
                  const allActive = modPerms.every((p) => activeDraft.permissions.includes(p.key));

                  // Any unparented non-page permissions
                  const unparentedPerms = modPerms.filter((p) => p.kind !== "page" && !p.parent_page);

                  return (
                    <Panel key={modKey}>
                      <PanelHeader
                        title={
                          <div className="flex items-center gap-2">
                            <modMeta.icon className="text-bank-muted size-4" aria-hidden="true" />
                            <span className="font-semibold">{modMeta.label}</span>
                          </div>
                        }
                        actions={
                          <div className="flex items-center gap-3">
                            <span className="text-bank-muted font-mono text-[11px]">
                              {modPerms.filter((p) => activeDraft.permissions.includes(p.key)).length} /{" "}
                              {modPerms.length} enabled
                            </span>
                            {pagePerms.length > 1 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleModulePages(modKey, !allPagesActive)}
                              >
                                {allPagesActive ? "Hide All Screens" : "Show All Screens"}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleModuleAll(modKey, !allActive)}
                            >
                              {allActive ? "Deselect All in Module" : "Select All in Module"}
                            </Button>
                          </div>
                        }
                      />

                      <div className="space-y-4 p-4">
                        {/* Screen Cards Stack */}
                        <div className="space-y-3.5">
                          {pagePerms.map((pagePerm) => {
                            const isPageActive = activeDraft.permissions.includes(pagePerm.key);
                            const isLocked =
                              activeRoleData.role === "admin" &&
                              (pagePerm.key === "page:access" || pagePerm.key === "access:roles_edit");

                            const childPerms = modPerms.filter((p) => p.parent_page === pagePerm.key);
                            const activeChildCount = childPerms.filter((p) =>
                              activeDraft.permissions.includes(p.key),
                            ).length;
                            const allChildrenActive =
                              childPerms.length > 0 && activeChildCount === childPerms.length;

                            return (
                              <div
                                key={pagePerm.key}
                                className={cn(
                                  "rounded-lg border transition-all",
                                  isPageActive
                                    ? "border-bank-border-strong bg-bank-surface shadow-2xs"
                                    : "border-bank-border bg-bank-surface-2/40 opacity-80",
                                )}
                              >
                                {/* Screen Card Header */}
                                <div className="border-bank-border flex flex-wrap items-center justify-between gap-3 border-b p-3 bg-bank-surface-2/30">
                                  <div className="min-w-0 flex-1 space-y-0.5">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold tracking-tight">{pagePerm.title}</span>
                                      <span className="text-bank-muted bg-bank-surface-3 rounded px-1.5 py-0.5 font-mono text-[10px]">
                                        {pagePerm.key}
                                      </span>
                                      {isLocked && (
                                        <span className="text-bank-muted border-bank-border bg-bank-surface inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px]">
                                          <Lock className="size-2.5" /> Required
                                        </span>
                                      )}
                                      <span
                                        className={cn(
                                          "rounded px-1.5 py-0.5 font-mono text-[10px] font-medium",
                                          isPageActive
                                            ? "bg-bank-good-wash text-bank-good-ink"
                                            : "bg-bank-surface-3 text-bank-muted",
                                        )}
                                      >
                                        {isPageActive ? "Screen Visible" : "Screen Hidden"}
                                      </span>
                                    </div>
                                    <p className="text-bank-muted text-[11px]">{pagePerm.description}</p>
                                  </div>

                                  <div className="flex shrink-0 items-center gap-3">
                                    {childPerms.length > 0 && isPageActive && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-bank-muted font-mono text-[10.5px]">
                                          {activeChildCount} / {childPerms.length} actions
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-[11px]"
                                          onClick={() => handleTogglePageChildAll(pagePerm.key, !allChildrenActive)}
                                        >
                                          {allChildrenActive ? "Deselect All Actions" : "Select All Actions"}
                                        </Button>
                                      </div>
                                    )}
                                    <Switch
                                      label={`Toggle visibility of ${pagePerm.title}`}
                                      checked={isPageActive}
                                      disabled={isLocked}
                                      onChange={() => handleTogglePermission(pagePerm.key)}
                                    />
                                  </div>
                                </div>

                                {/* Screen Actions Area */}
                                {childPerms.length > 0 ? (
                                  <div className="p-3">
                                    {!isPageActive ? (
                                      <div className="border-bank-border text-bank-muted rounded border border-dashed bg-bank-surface-2/30 p-2.5 text-center text-xs">
                                        Screen is hidden. Enable this screen above to activate and configure its {childPerms.length} granular actions.
                                      </div>
                                    ) : (
                                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {childPerms.map((child) => {
                                          const isReadChild = child.kind === "read";
                                          const checked = isReadChild ? true : activeDraft.permissions.includes(child.key);
                                          const isChildLocked =
                                            isReadChild ||
                                            (activeRoleData.role === "admin" &&
                                              (child.key === "access:roles_edit" || child.key === "page:access"));
                                          const isPurge = child.kind === "delete" && child.key.includes("purge");

                                          return (
                                            <label
                                              key={child.key}
                                              className={cn(
                                                "flex items-start gap-2.5 rounded-md border p-2.5 transition-colors select-none",
                                                checked
                                                  ? "border-bank-accent/40 bg-bank-accent-wash/5 text-bank-ink"
                                                  : "border-bank-border bg-bank-surface/60 text-bank-muted hover:border-bank-border-strong hover:text-bank-ink",
                                                isChildLocked ? "cursor-not-allowed opacity-90" : "cursor-pointer",
                                              )}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={isChildLocked}
                                                onChange={() => !isChildLocked && handleTogglePermission(child.key)}
                                                className="border-bank-border-strong text-bank-accent mt-0.5 size-3.5 rounded"
                                              />
                                              <div className="min-w-0 space-y-0.5">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                  <span
                                                    className={cn(
                                                      "text-xs font-medium",
                                                      isPurge && "text-bank-bad-ink",
                                                    )}
                                                  >
                                                    {child.title}
                                                  </span>
                                                  <span
                                                    className="text-bank-muted bg-bank-surface-2 rounded px-1 font-mono text-[9px] uppercase"
                                                    title={KIND_LABELS[child.kind] || child.kind}
                                                  >
                                                    {child.kind}
                                                  </span>
                                                  {isReadChild && (
                                                    <span className="inline-flex items-center gap-1 text-[9.5px] text-bank-accent bg-bank-accent-wash/20 border border-bank-accent/30 rounded px-1.5 py-0.5 font-medium">
                                                      <Lock className="size-2.5" aria-hidden="true" />
                                                      Included with screen
                                                    </span>
                                                  )}
                                                  {isChildLocked && !isReadChild && (
                                                    <Lock className="text-bank-muted size-2.5" aria-hidden="true" />
                                                  )}
                                                </div>
                                                <p className="text-bank-muted text-[10.5px] leading-snug">
                                                  {isReadChild ? "Baseline view access is included by default with screen access." : child.description}
                                                </p>
                                              </div>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-bank-muted p-3 text-xs italic">
                                    No additional actions for this screen.
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Fallback for unparented permissions */}
                          {unparentedPerms.length > 0 && (
                            <div className="border-bank-border bg-bank-surface rounded-lg border p-3.5 space-y-3">
                              <span className="text-xs font-semibold">Additional Module Actions</span>
                              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                                {unparentedPerms.map((child) => {
                                  const checked = activeDraft.permissions.includes(child.key);
                                  return (
                                    <label
                                      key={child.key}
                                      className={cn(
                                        "flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 transition-colors select-none",
                                        checked
                                          ? "border-bank-accent/40 bg-bank-accent-wash/5 text-bank-ink"
                                          : "border-bank-border bg-bank-surface/60 text-bank-muted hover:border-bank-border-strong hover:text-bank-ink",
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleTogglePermission(child.key)}
                                        className="border-bank-border-strong text-bank-accent mt-0.5 size-3.5 rounded"
                                      />
                                      <div className="min-w-0 space-y-0.5">
                                        <span className="text-xs font-medium">{child.title}</span>
                                        <p className="text-bank-muted text-[10.5px] leading-snug">
                                          {child.description}
                                        </p>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Panel>
                  );
                })}
              </div>
            )}

            {/* Tab 2: Assigned Members */}
            {activeTab === "members" && (
              <div className="space-y-5 p-5">
                <Panel>
                  <PanelHeader
                    title={
                      <div className="flex items-center gap-2">
                        <Users className="text-bank-muted size-4" aria-hidden="true" />
                        <span>Assigned Workspace Members ({activeDraft.members.length})</span>
                      </div>
                    }
                  />

                  <div className="space-y-4 p-4">
                    {/* Add Member Entry */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        {directoryReady ? (
                          <>
                            <FieldLabel htmlFor="add-members-picker">Add a Workspace member</FieldLabel>
                            <Combobox
                              id="add-members-picker"
                              value=""
                              placeholder="Search by name or member number…"
                              options={pickerOptions}
                              allowCustomValue
                              onChange={handlePickMember}
                            />
                          </>
                        ) : (
                          <>
                            <FieldLabel htmlFor="add-members-input">Add member numbers</FieldLabel>
                            <div className="flex gap-2">
                              <Input
                                id="add-members-input"
                                value={entry}
                                placeholder="1234, 5678, user-9"
                                onChange={(e) => setEntry(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddMembers();
                                  }
                                }}
                              />
                              <Button size="sm" disabled={!entry.trim()} onClick={handleAddMembers}>
                                Add
                              </Button>
                            </div>
                            {directory.data && !directory.data.available && (
                              <p className="text-bank-muted mt-1 text-[11px]">
                                Member search unavailable: {directory.data.detail ?? "Workspace could not be reached"}.
                              </p>
                            )}
                          </>
                        )}
                        {entryError && <Callout tone="bad">{entryError}</Callout>}
                      </div>

                      <div>
                        <FieldLabel htmlFor="role-note-input">Role Note (Internal context)</FieldLabel>
                        <Input
                          id="role-note-input"
                          value={activeDraft.note}
                          placeholder="What this role is for, department, or owner"
                          onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Member Search */}
                    <div className="border-bank-border flex items-center gap-2 border-t pt-4">
                      <div className="relative flex-1">
                        <Search className="text-bank-muted absolute top-2.5 left-2.5 size-3.5" aria-hidden="true" />
                        <Input
                          value={memberSearch}
                          placeholder="Filter members in this role…"
                          className="pl-8 text-xs"
                          onChange={(e) => setMemberSearch(e.target.value)}
                        />
                      </div>
                      <span className="text-bank-muted font-mono text-[11px] tabular-nums">
                        Showing {filteredMembers.length} of {activeDraft.members.length}
                      </span>
                    </div>

                    {/* Members Card Grid */}
                    {filteredMembers.length === 0 ? (
                      <div className="border-bank-border bg-bank-surface-2 rounded-lg border border-dashed p-6 text-center text-xs text-bank-muted">
                        No members assigned or matching filter.
                      </div>
                    ) : (
                      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredMembers.map((m) => {
                          const others = (memberRolesMap.get(fold(m)) ?? []).filter(
                            (t) => t.toLowerCase() !== activeRoleData.role.toLowerCase(),
                          );
                          const lockedSelf = activeRoleData.role === "admin" && isMe(m);
                          const known = directoryByNumber.get(fold(m));
                          const displayName = known?.full_name || (isMe(m) ? me.full_name : "");

                          return (
                            <div
                              key={fold(m)}
                              className="border-bank-border bg-bank-surface flex items-center justify-between rounded-lg border p-3 shadow-2xs"
                            >
                              <div className="flex min-w-0 items-center gap-2.5">
                                <div className="bg-bank-surface-3 text-bank-ink grid size-8 shrink-0 place-items-center rounded-full font-mono text-[11px] font-semibold">
                                  {(displayName || m).slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    {displayName ? (
                                      <span className="truncate text-xs font-semibold" title={displayName}>
                                        {known ? memberLabel(known) : displayName}
                                      </span>
                                    ) : (
                                      <span className="font-mono text-xs font-semibold">{m}</span>
                                    )}
                                    {isMe(m) && (
                                      <span className="bg-bank-accent-wash text-bank-accent-ink rounded px-1 font-mono text-[9px] font-medium">
                                        you
                                      </span>
                                    )}
                                  </div>
                                  {displayName && (
                                    <p className="text-bank-muted truncate font-mono text-[10px]">
                                      #{m}
                                      {known && managerLabel(known) ? ` · ${managerLabel(known)}` : ""}
                                    </p>
                                  )}
                                  {others.length > 0 && (
                                    <p className="text-bank-muted truncate text-[10px]" title={others.join(", ")}>
                                      also {others.join(", ")}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <button
                                type="button"
                                aria-label={`Remove member ${m}`}
                                disabled={lockedSelf}
                                title={
                                  lockedSelf
                                    ? "You cannot remove yourself from Admin"
                                    : `Remove ${m} from ${activeRoleData.title}`
                                }
                                onClick={() =>
                                  setDraft((d) => ({
                                    ...d,
                                    members: d.members.filter((x) => fold(x) !== fold(m)),
                                  }))
                                }
                                className="text-bank-muted hover:text-bank-bad-ink rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-30"
                              >
                                <X className="size-3.5" aria-hidden="true" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Panel>
              </div>
            )}

            {/* Tab 3: Comparison Matrix */}
            {activeTab === "matrix" && (
              <div className="space-y-4 p-5">
                <Panel>
                  <PanelHeader
                    title="Role Capability Comparison Matrix"
                    actions={
                      <div className="flex items-center gap-2">
                        <Segmented
                          label="Module filter"
                          value={matrixModuleFilter}
                          onChange={setMatrixModuleFilter}
                          options={moduleFilterOptions}
                        />
                      </div>
                    }
                  />

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-bank-border bg-bank-surface-2 border-b text-left">
                        <tr>
                          <th className="text-bank-muted px-4 py-2.5 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase">
                            Module & Capability
                          </th>
                          {roles.map((r) => (
                            <th
                              key={r.role}
                              className={cn(
                                "text-bank-muted px-3 py-2.5 text-center font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase",
                                r.role === activeRoleData.role && "bg-bank-accent-wash/30 text-bank-accent-ink",
                              )}
                            >
                              <div className="flex flex-col items-center">
                                <span>{r.title || r.role}</span>
                                <span className="text-[9px] font-normal lowercase opacity-75">
                                  ({r.members.length} members)
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-bank-border divide-y">
                        {catalog
                          .filter((p) => matrixModuleFilter === "all" || p.module === matrixModuleFilter)
                          .map((p) => {
                            const isPage = p.kind === "page";
                            const isChild = Boolean(p.parent_page);
                            return (
                              <tr
                                key={p.key}
                                className={cn(
                                  "hover:bg-bank-surface-2/40 transition-colors",
                                  isPage ? "bg-bank-surface-2/30 font-semibold" : "bg-bank-surface",
                                )}
                              >
                                <td className={cn("px-4 py-2", isChild && "pl-8")}>
                                  <div className="flex items-baseline gap-2">
                                    {isChild && <span className="text-bank-muted font-mono text-xs">↳</span>}
                                    <span className={cn(isPage ? "font-semibold" : "font-normal")}>{p.title}</span>
                                    <span className="text-bank-muted font-mono text-[10px]">({p.key})</span>
                                    {!isPage && (
                                      <span
                                        className="text-bank-muted bg-bank-surface-2 rounded px-1 font-mono text-[9px] uppercase"
                                        title={KIND_LABELS[p.kind] || p.kind}
                                      >
                                        {p.kind}
                                      </span>
                                    )}
                                  </div>
                                  <p className={cn("text-bank-muted text-[11px]", isChild && "pl-3")}>{p.description}</p>
                                </td>

                                {roles.map((r) => {
                                  const effectivePerms =
                                    r.role === activeRoleData.role ? activeDraft.permissions : r.permissions;
                                  const holds =
                                    effectivePerms.includes(p.key) ||
                                    (p.kind === "read" && p.parent_page && effectivePerms.includes(p.parent_page));

                                  return (
                                    <td
                                      key={r.role}
                                      className={cn(
                                        "px-3 py-2 text-center",
                                        r.role === activeRoleData.role && "bg-bank-accent-wash/10",
                                      )}
                                    >
                                      {holds ? (
                                        <span className="bg-bank-good-wash text-bank-good-ink inline-grid size-5 place-items-center rounded-full font-bold">
                                          <Check className="size-3" aria-label="Granted" />
                                        </span>
                                      ) : (
                                        <span className="text-bank-muted font-mono opacity-40">—</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>
            )}
          </main>
        </div>
      )}

      {/* Modal: Create Role */}
      <CreateRoleModal
        open={createModalOpen}
        roles={roles}
        isPending={createRole.isPending}
        error={createRole.error}
        onClose={() => setCreateModalOpen(false)}
        onCreate={(data) => {
          createRole.mutate(data, {
            onSuccess: (newRole) => {
              setCreateModalOpen(false);
              setSelectedRole(newRole.role);
            },
          });
        }}
      />

      {/* Dialog: Delete Custom Role */}
      <ConfirmDialog
        open={deleteTargetRole !== null}
        title={`Delete Role "${deleteTargetRole?.title || deleteTargetRole?.role}"`}
        confirmLabel="Delete Role"
        busy={deleteRole.isPending}
        error={deleteRole.error}
        onCancel={() => setDeleteTargetRole(null)}
        onConfirm={() => {
          if (!deleteTargetRole) return;
          deleteRole.mutate(deleteTargetRole.role, {
            onSuccess: () => {
              setDeleteTargetRole(null);
              setSelectedRole("admin");
            },
          });
        }}
      >
        <div className="space-y-2 text-xs">
          <p>
            Are you sure you want to delete this custom role? This will permanently remove the role and all its
            permission definitions.
          </p>
          {deleteTargetRole && deleteTargetRole.members.length > 0 && (
            <p className="text-bank-bad-ink font-semibold">
              Warning: {deleteTargetRole.members.length} members are currently assigned to this role and will lose these
              permissions.
            </p>
          )}
        </div>
      </ConfirmDialog>

      {/* Dialog: Reset Role Defaults */}
      <ConfirmDialog
        open={resetDefaultsRole !== null}
        title={`Reset "${resetDefaultsRole?.title || resetDefaultsRole?.role}" to System Defaults?`}
        confirmLabel="Reset to Defaults"
        busy={resetDefaults.isPending}
        error={resetDefaults.error}
        onCancel={() => setResetDefaultsRole(null)}
        onConfirm={() => {
          if (!resetDefaultsRole) return;
          resetDefaults.mutate(resetDefaultsRole.role, {
            onSuccess: () => {
              setResetDefaultsRole(null);
              setRoleDrafts((prev) => {
                const next = { ...prev };
                delete next[resetDefaultsRole.role];
                return next;
              });
            },
          });
        }}
      >
        <p className="text-xs">
          This will restore the standard factory preset permissions for{" "}
          <strong>{resetDefaultsRole?.title || resetDefaultsRole?.role}</strong>. Any custom permission toggles made to
          this role will be overwritten.
        </p>
      </ConfirmDialog>
    </div>
  );
}
